import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/ui/components/primitives/dialog";
import { Badge } from "@/ui/components/primitives/badge";
import { Button } from "@/ui/components/primitives/button";
import { Textarea } from "@/ui/components/primitives/textarea";
import { Separator } from "@/ui/components/primitives/separator";
import { cn } from "@/ui/components/primitives/utils";
import { Edit, Copy, Check } from "lucide-react";
import { approvePayout, rejectPayout, type PayoutApproval } from "@/services/api/services/admin-payouts";
import { showSuccessToast, showErrorToast } from "@/services/toast-helper";
import { UpdatePayoutModal } from "./update-payout-modal";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  payout: PayoutApproval | null;
  onActionComplete?: () => void;
  userRole?: string;
};

function PayoutStatusBadge({ status }: { status: string }) {
  const statusLower = status.toLowerCase();
  const variants: Record<string, string> = {
    pending: "bg-warning/10 text-warning border-warning/20",
    processing: "bg-primary/10 text-primary border-primary/20",
    queued: "bg-primary/10 text-primary border-primary/20",
    success: "bg-success/10 text-success border-success/20",
    failed: "bg-destructive/10 text-destructive border-destructive/20",
    cancelled: "bg-muted text-muted-foreground border-border",
  };

  return (
    <Badge className={cn(variants[statusLower] || variants.pending)}>
      {status}
    </Badge>
  );
}

export function PayoutDetailModal({ open, onOpenChange, payout, onActionComplete, userRole }: Props) {
  const [loading, setLoading] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  if (!payout) return null;

  const handleApprove = async () => {
    setLoading(true);
    try {
      await approvePayout(payout.id);
      showSuccessToast("Payout approved successfully");
      onOpenChange(false);
      onActionComplete?.();
    } catch (error: any) {
      const message = error?.detail?.error?.message || error?.message || "Failed to approve payout";
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      showErrorToast("Please provide a reason for rejection");
      return;
    }
    setLoading(true);
    try {
      await rejectPayout(payout.id, rejectReason);
      showSuccessToast("Payout rejected");
      setShowRejectForm(false);
      setRejectReason("");
      onOpenChange(false);
      onActionComplete?.();
    } catch (error: any) {
      const message = error?.detail?.error?.message || error?.message || "Failed to reject payout";
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  };

  const isPending = payout.status.toUpperCase() === "PENDING";
  const isAdmin = userRole === "SUPER_ADMIN" || userRole === "ADMIN" || userRole === "SUPPORT";

  // Check if this is a crypto transaction
  const isCryptoTransaction = payout.beneficiary?.accountType === "WALLET" ||
                              payout.beneficiary?.accountType === "CRYPTO" ||
                              payout.beneficiaryDetails?.walletAddress ||
                              (payout.currency === "USDT" && !payout.beneficiary?.accountNumber);

  const handleCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      showSuccessToast(`${fieldName} copied to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      showErrorToast("Failed to copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Payout Request Details</span>
            <PayoutStatusBadge status={payout.status} />
          </DialogTitle>
          {/* <DialogDescription>
            <span className="font-mono text-xs">{payout.reference || payout.id}</span>
          </DialogDescription> */}
        </DialogHeader>

        {/* Amount Section */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Amount</p>
            <p className="font-semibold text-lg">
              {payout.currency === "USDT" ? "$" : "₹"}{payout.amount.toLocaleString()} {payout.currency}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Fees</p>
            <p className="text-sm">₹{payout.fees.toLocaleString()}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Net Amount</p>
            <p className="text-sm font-medium">
              {payout.currency === "USDT" ? "$" : "₹"}{payout.netAmount.toLocaleString()}
            </p>
          </div>
        </div>

        <Separator />

        {/* Merchant Info - Only show to admins */}
        {isAdmin && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">Merchant</p>
            <div className="bg-muted/50 rounded-md p-3">
              <p className="font-medium">{payout.merchantName || payout.merchant?.name || 'Unknown Merchant'}</p>
              <p className="text-xs text-muted-foreground font-mono">{payout.merchantId || payout.merchant?.id || 'N/A'}</p>
            </div>
          </div>
        )}

        {/* Beneficiary Info */}
        <div>
          <p className="text-xs text-muted-foreground mb-2">Beneficiary</p>
          <div className="bg-muted/50 rounded-md p-3 space-y-3">
            <div className="space-y-2">
              <p className="font-medium">
                {payout.beneficiaryDetails?.name || payout.beneficiary?.name || 'Unknown Beneficiary'}
              </p>
              <div className="space-y-1 text-sm">
                {/* Crypto Transaction Fields */}
                {isCryptoTransaction && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Wallet Address:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs break-all max-w-[200px]">
                          {payout.beneficiaryDetails?.walletAddress || payout.beneficiary?.accountNumber || 'N/A'}
                        </span>
                        {(payout.beneficiaryDetails?.walletAddress || payout.beneficiary?.accountNumber) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopy(
                              payout.beneficiaryDetails?.walletAddress || payout.beneficiary?.accountNumber,
                              "Wallet Address"
                            )}
                            className="h-6 w-6 p-0"
                          >
                            {copiedField === "Wallet Address" ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Network/IFSC field for crypto */}
                    {(payout.beneficiaryDetails?.network || payout.beneficiary?.ifscCode) && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Network:</span>
                        <div className="flex items-center gap-2">
                          <span>{payout.beneficiaryDetails?.network || payout.beneficiary?.ifscCode}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopy(
                              payout.beneficiaryDetails?.network || payout.beneficiary?.ifscCode,
                              "Network"
                            )}
                            className="h-6 w-6 p-0"
                          >
                            {copiedField === "Network" ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Bank Transaction Fields */}
                {!isCryptoTransaction && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Account Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs break-all max-w-[200px]">
                          {payout.beneficiaryDetails?.accountNumber || payout.beneficiary?.accountNumber || 'N/A'}
                        </span>
                        {(payout.beneficiaryDetails?.accountNumber || payout.beneficiary?.accountNumber) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopy(
                              payout.beneficiaryDetails?.accountNumber || payout.beneficiary?.accountNumber,
                              "Account Number"
                            )}
                            className="h-6 w-6 p-0"
                          >
                            {copiedField === "Account Number" ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">IFSC Code:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">
                          {payout.beneficiaryDetails?.ifscCode || payout.beneficiary?.ifscCode || 'N/A'}
                        </span>
                        {(payout.beneficiaryDetails?.ifscCode || payout.beneficiary?.ifscCode) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopy(
                              payout.beneficiaryDetails?.ifscCode || payout.beneficiary?.ifscCode,
                              "IFSC Code"
                            )}
                            className="h-6 w-6 p-0"
                          >
                            {copiedField === "IFSC Code" ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Bank Name:</span>
                      <div className="flex items-center gap-2">
                        <span>{payout.beneficiaryDetails?.bankName || payout.beneficiary?.bankName || 'N/A'}</span>
                        {(payout.beneficiaryDetails?.bankName || payout.beneficiary?.bankName) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopy(
                              payout.beneficiaryDetails?.bankName || payout.beneficiary?.bankName,
                              "Bank Name"
                            )}
                            className="h-6 w-6 p-0"
                          >
                            {copiedField === "Bank Name" ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Account Type */}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Account Type:</span>
                  <span className="capitalize">
                    {payout.beneficiaryDetails?.accountType || payout.beneficiary?.accountType || 'N/A'}
                  </span>
                </div>

                {/* Additional beneficiary info */}
                {payout.beneficiary?.email && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Email:</span>
                    <span className="text-sm">{payout.beneficiary.email}</span>
                  </div>
                )}
                {payout.beneficiary?.phone && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Phone:</span>
                    <span className="text-sm">{payout.beneficiary.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Details Grid */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Purpose</p>
            <p className="text-sm">{payout.purpose || "-"}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Mode</p>
            <p className="text-sm">{payout.mode || "-"}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Reference</p>
            <p className="text-sm break-all">{payout.reference || "-"}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Method</p>
            <p className="text-sm">{payout.method || "-"}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Created</p>
            <p className="text-sm">
              {payout.createdAt ? (() => {
                try {
                  const date = new Date(payout.createdAt);
                  return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
                } catch {
                  return 'Invalid Date';
                }
              })() : 'N/A'}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Updated</p>
            <p className="text-sm">
              {payout.updatedAt ? (() => {
                try {
                  const date = new Date(payout.updatedAt);
                  return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
                } catch {
                  return 'Invalid Date';
                }
              })() : 'N/A'}
            </p>
          </div>
        </div>

        {payout.notes && (
          <>
            <Separator />
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Notes</p>
              <p className="text-sm whitespace-pre-wrap">{payout.notes}</p>
            </div>
          </>
        )}

        {/* Reject Form */}
        {isAdmin && showRejectForm && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-sm font-medium text-destructive">Rejection Reason</p>
              <Textarea
                placeholder="Enter reason for rejection..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
              />
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigator.clipboard.writeText(payout.reference || payout.id)}
          >
            Copy Reference ID
          </Button>

          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUpdateModal(true)}
              disabled={loading}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit Details
            </Button>
          )}

          {isAdmin && isPending && !showRejectForm && (
            <>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowRejectForm(true)}
                disabled={loading}
              >
                Reject
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleApprove}
                disabled={loading}
                className="bg-success hover:bg-success/90"
              >
                {loading ? "Approving..." : "Approve"}
              </Button>
            </>
          )}

          {isAdmin && showRejectForm && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectReason("");
                }}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleReject}
                disabled={loading || !rejectReason.trim()}
              >
                {loading ? "Rejecting..." : "Confirm Reject"}
              </Button>
            </>
          )}

          {(!isAdmin || !isPending) && (
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </div>
      </DialogContent>

      {/* Update Payout Modal */}
      <UpdatePayoutModal
        open={showUpdateModal}
        onOpenChange={setShowUpdateModal}
        payout={payout}
        onUpdateComplete={() => {
          setShowUpdateModal(false);
          onActionComplete?.();
        }}
      />
    </Dialog>
  );
}
